"use client";

import { useState, PropsWithChildren } from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { ThemeProvider, useTheme } from "next-themes";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster, toast } from "sonner";
import { Toaster as RadixToaster } from "@/components/ui/toaster";
import { SessionProvider } from "next-auth/react";
import { onCircuitBreakerRecover } from "@/lib/api/fetchWithStatus";
import { ClientErrorReporterProvider } from "@/components/ClientErrorReporterProvider";
import { PostHogTracker } from "@/components/PostHogProvider";
import { captureException } from "@/lib/posthog";
import { track } from "@/lib/analytics";
import { ReEngageApiError } from "@/lib/reEngageErrors";
import { REENGAGE_MUTATION_META } from "@/hooks/useStartReEngageRun";
import { Suspense } from "react";
import { installDomMutationGuard } from "@/lib/domMutationGuard";
import { CALL_SUMMARY_TOASTER_ID } from "@/components/calls/summary/types";

// Module scope, not an effect: React's commit phase can hit a translated DOM on
// the very first render, before any effect would have run.
installDomMutationGuard();

type ProvidersProps = PropsWithChildren;

/** Extract HTTP status from FetchError (.status), ClientApiError (.statusCode), or ReEngageApiError (.httpStatus). */
function getErrorStatus(error: unknown): number | undefined {
  if (typeof error === "object" && error !== null) {
    const e = error as { statusCode?: number; status?: number; httpStatus?: number };
    return e.statusCode ?? e.status ?? e.httpStatus;
  }
  return undefined;
}

/**
 * True for transient CLIENT-SIDE connectivity failures — the user's network
 * dropped, they went offline, or the server was momentarily unreachable. The app
 * already handles these (retry/backoff + circuit breaker), so they are NOT
 * actionable code bugs. Reporting them to Error Tracking is what let a single
 * user's network blip (dozens of concurrent queries rejecting at once) flood the
 * dashboard and trip the exception-spike alert (PostHog triage 2026-06-09).
 *
 * Deliberately NARROW — only unambiguous connectivity signatures, NOT the whole
 * `status===undefined` branch (which must keep catching real unknown breakages):
 *  - ClientApiError from clientApi.ts wraps a fetch TypeError as code NETWORK_ERROR
 *    (synthetic 503), so match the code regardless of the synthetic status.
 *  - Raw fetch rejections in non-clientApi queryFns surface as a TypeError with a
 *    browser-specific message ("Failed to fetch" Chrome, "Load failed" Safari,
 *    "NetworkError..." Firefox).
 */
function isTransientNetworkError(
  error: unknown,
  code: string | undefined,
  status: number | undefined,
): boolean {
  // `code` is paired with the synthetic 503 to avoid colliding with the unrelated
  // NETWORK_ERROR call-outcome enum value some routes return in error envelopes.
  if (code === "NETWORK_ERROR" && status === 503) return true;
  if (typeof error !== "object" || error === null) return false;
  const e = error as { name?: string; message?: string };
  return (
    e.name === "TypeError" &&
    /failed to fetch|load failed|networkerror|network request failed/i.test(e.message || "")
  );
}

/**
 * Global telemetry for ALL react-query failures — the single chokepoint that
 * gives us 100% coverage of handled query/mutation errors (nearly every data
 * flow runs through react-query). Called from QueryCache/MutationCache.onError.
 *
 * Routing:
 *  - Aborted requests (navigation/unmount), 401 (session expired — handled by
 *    the AUTH_EXPIRED flow), and 429 (rate-limited — already surfaced by the
 *    circuit breaker) are skipped as non-actionable noise.
 *  - 5xx / unknown-status → PostHog Error Tracking (captureException), where we
 *    triage real breakages first.
 *  - Other 4xx → a typed `api_request_failed` event for analytics/alerting,
 *    kept out of Error Tracking to avoid drowning real bugs in expected 4xx.
 */
// WI-15: one toast per window, not one per failed request — an unverified
// user starting a dialer run can trip several gated calls at once, and the
// persistent VerifyEmailBanner is already the standing explanation.
let lastEmailUnverifiedToastAt = 0;
const EMAIL_UNVERIFIED_TOAST_THROTTLE_MS = 30_000;

function maybeToastEmailUnverified(status: number | undefined, code: string | undefined) {
  if (status !== 403 || code !== "EMAIL_UNVERIFIED") return;
  const now = Date.now();
  if (now - lastEmailUnverifiedToastAt < EMAIL_UNVERIFIED_TOAST_THROTTLE_MS) return;
  lastEmailUnverifiedToastAt = now;
  toast.warning("Verify your email to do this", {
    description:
      "Check your inbox for the verification link. Just verified? Give it a minute.",
  });
}

// decision 29: any write that hits a module paywall (403 MODULE_REQUIRED) gets
// one explicit "this needs the CRM module" toast with a way to buy it — the
// glassed pages explain themselves, this covers the writes that aren't glassed
// (dashboard reschedule, follow-ups picker, drip start, automation publish).
let lastModuleRequiredToastAt = 0;
const MODULE_REQUIRED_TOAST_THROTTLE_MS = 30_000;

function maybeToastModuleRequired(status: number | undefined, code: string | undefined, error: unknown) {
  if (status !== 403 || code !== "MODULE_REQUIRED") return;
  const now = Date.now();
  if (now - lastModuleRequiredToastAt < MODULE_REQUIRED_TOAST_THROTTLE_MS) return;
  lastModuleRequiredToastAt = now;
  const userMessage =
    typeof error === "object" && error !== null
      ? (error as { userMessage?: string }).userMessage
      : undefined;
  toast.warning(userMessage || "This is part of the CRM module", {
    description: "Add it to your plan to keep going.",
    action: { label: "See plans", onClick: () => { window.location.href = "/settings/billing/subscribe"; } },
  });
}

function reportReactQueryError(
  error: unknown,
  meta: { kind: "query" | "mutation"; key?: string; feature?: string },
): void {
  const name =
    typeof error === "object" && error !== null
      ? (error as { name?: string }).name
      : undefined;
  if (name === "AbortError") return;

  const status = getErrorStatus(error);
  if (status === 401 || status === 429) return;

  const code =
    typeof error === "object" && error !== null
      ? (error as { code?: string }).code
      : undefined;
  // ClientApiError carries the request's X-Request-Id — the key into backend logs.
  const requestId =
    typeof error === "object" && error !== null
      ? (error as { requestId?: string }).requestId
      : undefined;

  maybeToastEmailUnverified(status, code);
  maybeToastModuleRequired(status, code, error);

  // Transient connectivity failures are handled by the app's retry/circuit-breaker
  // path — route them to analytics (not Error Tracking) so a single user's network
  // blip can't drown real bugs or trip the exception-spike alert.
  if (isTransientNetworkError(error, code, status)) {
    track("api_request_failed", {
      kind: meta.kind,
      key: meta.key,
      feature: meta.feature,
      status,
      code,
      requestId,
      reason: "network",
    });
    return;
  }

  if (status === undefined || status >= 500) {
    captureException(error, {
      source: "react-query",
      kind: meta.kind,
      key: meta.key,
      feature: meta.feature,
      status,
      code,
      requestId,
    });
  } else {
    track("api_request_failed", {
      kind: meta.kind,
      key: meta.key,
      feature: meta.feature,
      status,
      code,
      requestId,
    });
  }
}

function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return <Toaster theme={resolvedTheme === "dark" ? "dark" : "light"} />;
}

export default function Providers({ children }: ProvidersProps) {
  const [qc] = useState(() => {
    // Forward-declared so QueryCache/MutationCache onError handlers can call
    // client.invalidateQueries even though they're constructed before the
    // client itself. Standard TanStack pattern for cross-query coordination.
    // Bryan review #2: typed as `| undefined` with a runtime guard rather
    // than `!` definite-assignment so we don't silently dereference undefined
    // if an onError fires before the client assignment (practically impossible
    // — onError fires from queries that need a dispatching client — but the
    // explicit guard documents the invariant rather than relying on it).
    let client: QueryClient | undefined;

    // Re-Engage AUTH_EXPIRED → invalidate the page's feature-flag query so
    // the channel page button re-derives state after the session expires.
    // Scoping rationale:
    //   - Queries: filter on `query.queryKey[0] === "reEngage"` so only
    //     Re-Engage queries trigger the invalidation. Other features can
    //     produce 401s without disturbing Re-Engage's cache.
    //   - Mutations: MutationCache.onError doesn't receive a queryKey, so
    //     scope on `mutation.options.meta?.feature === REENGAGE_MUTATION_META.feature`.
    //     Re-Engage mutations import REENGAGE_MUTATION_META from a shared
    //     constant (see useStartReEngageRun.ts) so writer/reader are
    //     compile-time linked rather than string-matched.
    // Synchronous AUTH_EXPIRED rendering on the page lives in the union
    // predicate (shouldRenderAuthExpired) — invalidation here is the
    // cache-cleanup leg of the same defense, not a substitute for it.
    const queryCache = new QueryCache({
      onError: (error, query) => {
        reportReactQueryError(error, {
          kind: "query",
          key: Array.isArray(query.queryKey) ? String(query.queryKey[0]) : undefined,
        });
        if (!client) return;
        if (
          error instanceof ReEngageApiError &&
          error.code === "AUTH_EXPIRED" &&
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === "reEngage"
        ) {
          client.invalidateQueries({
            queryKey: ["reEngage", "featureFlag"],
          });
        }
      },
    });
    const mutationCache = new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        reportReactQueryError(error, {
          kind: "mutation",
          feature: (mutation.options.meta as { feature?: string } | undefined)?.feature,
          key: Array.isArray(mutation.options.mutationKey)
            ? String(mutation.options.mutationKey[0])
            : undefined,
        });
        if (!client) return;
        if (
          error instanceof ReEngageApiError &&
          error.code === "AUTH_EXPIRED" &&
          (mutation.options.meta as { feature?: string } | undefined)?.feature ===
            REENGAGE_MUTATION_META.feature
        ) {
          client.invalidateQueries({
            queryKey: ["reEngage", "featureFlag"],
          });
        }
      },
    });

    client = new QueryClient({
      queryCache,
      mutationCache,
      defaultOptions: {
        queries: {
          // Data stays fresh for 30s — prevents refetch storms on re-renders and tab switches
          staleTime: 30_000,
          // Don't refetch on window focus by default — opt in per query where needed
          refetchOnWindowFocus: false,
          // Don't retry 429s — retrying rate-limited requests makes the storm worse.
          // Don't retry 401s — session expired, retrying produces a storm of useless requests
          // until the user re-auths. Explicit early-return so the intent is obvious.
          // Allow transient 4xx (408 timeout, 409 conflict) one retry.
          // Skip all other 4xx (not transient).
          retry: (failureCount, error) => {
            const status = getErrorStatus(error);
            if (status === 429) return false;
            if (status === 401) return false;
            if (status === 408 || status === 409) return failureCount < 1;
            if (status && status >= 400 && status < 500) return false;
            return failureCount < 2;
          },
          // Back off longer on retries (2s, 4s) to reduce burst pressure
          retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 30000),
        },
      },
    });
    // Mark queries stale WITHOUT a synchronized app-wide refetch burst. The
    // unfiltered invalidateQueries() refetched EVERY active query at once on
    // recovery; if that burst re-tripped the per-user limiter the breaker
    // re-armed → self-sustaining storm (inbox 429 self-trip, Tier 2). Each
    // surface now recovers on its next natural trigger (interval/focus/mount);
    // surfaces needing prompt recovery (e.g. dialer-status) register their own
    // targeted onCircuitBreakerRecover callback — now additive, not overwriting.
    onCircuitBreakerRecover(() => client.invalidateQueries({ refetchType: "none" }));
    return client;
  });
  return (
    <SessionProvider refetchOnWindowFocus={false}>
      {/* Side-effects-only tracker (no children). Wrapped in Suspense
          because it uses useSearchParams — without the boundary, Next
          would bail the entire tree out of static rendering. */}
      <Suspense fallback={null}>
        <PostHogTracker />
      </Suspense>
      <ClientErrorReporterProvider>
        <QueryClientProvider client={qc}>
          <ThemeProvider attribute="class" defaultTheme="dark" enableSystem disableTransitionOnChange>
            <TooltipProvider>{children}</TooltipProvider>
            <ThemedToaster />
            {/* SMS blast progress banner (SmsBroadcastProgress, toasterId "sms-broadcast").
                z-30 on purpose: UNDER the softphone (z-40) and the incoming-call card (z-50),
                so blast progress never covers a call. bottom 80 clears the support chat
                bubble (fixed bottom-6 right-6 z-50) — same clearance DialerLauncher uses. */}
            <Toaster
              id="sms-broadcast"
              style={{ zIndex: 30 }}
              offset={{ bottom: 80 }}
              mobileOffset={{ bottom: 80 }}
              containerAriaLabel="SMS blast progress"
            />
            {/* Post-call AI summary popup (useCallSummaryPopup). Its OWN toaster so no other
                toast stacks in front of it — on the default toaster, hover-expand pushed its
                header and X off-screen. top-right + z-30 keeps it off the softphone corner and
                UNDER calls (softphone z-40, incoming-call card z-50). top 64 clears the mobile
                app header (sticky top-0 z-40 h-14) and the minimized-call pill. visibleToasts
                1: a newer summary shows on top, closing it brings back the previous one.
                🚨 Removing this or changing its id silently hides every summary popup. */}
            <Toaster
              id={CALL_SUMMARY_TOASTER_ID}
              position="top-right"
              visibleToasts={1}
              style={{ zIndex: 30 }}
              offset={{ top: 64 }}
              mobileOffset={{ top: 64 }}
              containerAriaLabel="Call summaries"
            />
            <RadixToaster />
          </ThemeProvider>
        </QueryClientProvider>
      </ClientErrorReporterProvider>
    </SessionProvider>
  );
}
